import { trpc } from "#/lib/trpc/client"
import { useQuery, type QueryClient, type UseQueryResult } from "@tanstack/react-query"

export type IntakeFormsData = Awaited<ReturnType<typeof trpc.telemedicine.getIntakeForms.query>>

const intakeFormsQueryKey = (appointmentId: string): unknown[] => ["telemedicine", "getIntakeForms", appointmentId]

// `staleTime: Infinity` keeps the list stable so a submit doesn't refetch and re-serve a form the
// member just completed before its completion webhook reaches carrot-clinic.
export function useIntakeForms(appointmentId: string, enabled: boolean): UseQueryResult<IntakeFormsData> {
  return useQuery({
    queryKey: intakeFormsQueryKey(appointmentId),
    queryFn: () => trpc.telemedicine.getIntakeForms.query({ appointmentId }),
    enabled: enabled && Boolean(appointmentId),
    refetchOnWindowFocus: false,
    staleTime: Infinity
  })
}

// Drops a completed form from the cached list so progress survives a page remount and the form isn't
// re-served while its completion webhook is still in flight.
export function removeCompletedIntakeForm(queryClient: QueryClient, appointmentId: string, formId: string): void {
  queryClient.setQueryData<IntakeFormsData>(intakeFormsQueryKey(appointmentId), (data) =>
    data ? { ...data, forms: data.forms.filter((form) => form.id !== formId) } : data
  )
}
